import { useState } from "react";
import { useTheme, styled } from "@mui/material/styles";
import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  Link,
  TextField,
  Paper,
} from "@mui/material";
import * as yup from "yup";

// Project imports
import AuthFooter from "components/AuthFooter";
import buildRoute from "../components/buildRoute";
import { getCsrfToken } from "../utils/csrf";

const emailSchema = yup.object().shape({
  email: yup
    .string()
    .email("Please enter a valid email address")
    .required("Email is required"),
});

const PageWrapper = styled("div")(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark"
      ? theme.palette.background.default
      : theme.palette.primary.light,
  minHeight: "100vh",
}));

const ForgotPassword = () => {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down("md"));
  const csrfToken = getCsrfToken();

  // hooks
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const [success, setsuccess] = useState(false);


  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(false);
    setsuccess(false);

    // Validate email
    try {
      await emailSchema.validate({ email });
    } catch (error) {
      return setError(error.message);
    }

    // Send reset password request
    try {
      const response = await fetch("/api/forgot-password-token/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'X-CSRFToken': csrfToken,
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        return setError("Failed to send reset password email");
      }

      setsuccess(true);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <PageWrapper>
      <Grid
        container
        direction="column"
        justifyContent="flex-end"
        sx={{ minHeight: "100vh" }}
      >
        <Grid item xs={12}>
          <Grid
            container
            justifyContent="center"
            alignItems="center"
            sx={{ minHeight: "calc(100vh - 68px)" }}
          >
            <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
              <Paper
                square
                sx={{
                  maxWidth: { xs: 400, lg: 450 },
                  margin: { xs: 2.5, md: 3 },
                  "& > *": {
                    flexGrow: 1,
                    flexBasis: "50%",
                  },
                }}
              >
                <Box
                  sx={{
                    bgcolor: "primary.main",
                    color: "primary.light",
                    px: 2,
                    py: 3,
                    textAlign: "center",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="h1"
                      color="inherit"
                      sx={{ fontWeight: 600 }}
                    >
                      PAD
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    color="inherit"
                    sx={{ fontWeight: 500 }}
                  >
                    From Community Tech Alliance
                  </Typography>
                </Box>
                <Box sx={{ p: { xs: 2, sm: 3, lg: 4, xl: 5 } }}>
                  <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction={matchDownSM ? "column-reverse" : "row"}
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Grid item>
                          <Stack
                            alignItems="center"
                            justifyContent="center"
                            spacing={1}
                          >
                            <Typography
                              variant="caption"
                              fontSize="16px"
                              color="inherit"
                              textAlign={matchDownSM ? "center" : "inherit"}
                              sx={{ fontWeight: "bold" }} // Add this line to make the text bold
                            >
                              Reset your password
                            </Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid
                        container
                        direction="column"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Grid item xs={12}>
                          <Box
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 2,
                                width: "100%",
                            }}
                            >
                            <TextField
                                label="Email Address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                            />

                            {error && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ textAlign: "left", mt: -1, width: "100%" }} // Align text to the left
                              >
                                {error}
                              </Typography>
                            )}

                            {success && (
                              <Typography
                                variant="caption"
                                color="success"
                                sx={{ textAlign: "left", mt: -1, width: "100%" }} // Align text to the left
                              >
                                A reset link will be sent to your recovery email if the email provided is associated with an account.
                              </Typography>
                            )}

                            <Button type="submit" variant="contained" color="primary" fullWidth>
                              Reset Password
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  maxWidth: { xs: 400, lg: 450 },
                  margin: { xs: 2.5, md: 3 },
                }}
              >
                <Typography
                  variant="caption"
                  fontSize="12px"
                  color="inherit"
                  textAlign="center"
                >
                  Trouble logging in? Check our{" "}
                  <Link href="https://cta.statuspage.io/" target="_blank">
                    Status Page
                  </Link>
                  &nbsp;or click the &apos;Help&apos; button below
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ m: 3, mt: 1 }}>
          <AuthFooter />
        </Grid>
      </Grid>
    </PageWrapper>
  );
};

const routeArgs = {
  element: <ForgotPassword />,
};

export default buildRoute(routeArgs);

